// External Numbers — a mobile, or a phone on another system, ringing alongside the extensions.
//
// Kevin, 2026-08-26: "Separate out the 'Also rings these numbers'... and also make them a
// destination with their own card." Then: "just keep it simple not 'Also rings these..', just
// 'External Numbers'...keep it simple."
//
// SO THE CARD SAYS WHAT IT IS AND STOPS. The heading was a sentence, the caption was three lines
// explaining outbound routes and registration, and the empty state re-explained the extensions. All
// of it true, none of it what somebody opening this card wants. A card with a noun for a heading and
// one line under it is readable at a glance; the plumbing belongs in the code, which is where it now
// lives.
//
// ITS OWN CARD, YES. It was a section inside the extensions table, which put two different kinds of
// thing under one heading and made the extensions card the place you go to manage something that is
// not an extension. Equal billing is right.
//
// A DESTINATION, NO — and the distinction is what keeps this tab comprehensible rather than being
// pedantry. A line has exactly ONE destination: the thing the inbound route points at. These numbers
// are MEMBERS of the ring group that destination names. If they were a second destination, a line
// would have two, and "what answers when someone dials this number" would stop having one answer —
// which is the question the whole tab exists to answer. So: its own card, at the same level as
// Extensions, both of them showing what is INSIDE the one destination.
//
// IT IS ALWAYS PRESENT, and says for itself when these numbers do not ring today.
//
// It was hidden unless the destination was a ring group, which read as "this line cannot do that"
// rather than "this line is not doing that" — and an absent card is indistinguishable from an empty
// one, so an operator could not tell whether a number was already configured. Saying "these will not
// ring until…" is one sentence and answers both questions.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

export default function ExternalRingCard({ line, canEdit, ringsToday = true }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');

  const q = useQuery({
    queryKey: ['line', String(line.id), 'extensions'],
    queryFn: () => api.get(`/lines/${line.id}/extensions`),
  });
  const externals = q.data?.external_members || [];

  const done = () => {
    qc.invalidateQueries({ queryKey: ['line', String(line.id), 'extensions'] });
    qc.invalidateQueries({ queryKey: ['line', String(line.id)] });
  };

  const save = useMutation({
    mutationFn: ({ num, lbl, position }) => api.put(
      `/lines/${line.id}/external-members/${encodeURIComponent(num)}`, { label: lbl, position },
    ),
    onSuccess: () => { setAdding(false); setNumber(''); setLabel(''); done(); },
  });
  const drop = useMutation({
    mutationFn: (memberId) => api.delete(`/lines/${line.id}/external-members/${memberId}`),
    onSuccess: done,
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">External Numbers</h3>
          <p className="mt-1 text-xs text-gray-500">
            A mobile, or a phone on another system. Applied on the next build.
          </p>
        </div>
        {canEdit && !adding && (
          <button
            type="button"
            onClick={() => { save.reset(); setAdding(true); }}
            className="flex-shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Add
          </button>
        )}
      </div>

      {externals.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100">
          {externals.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 py-2">
              <div>
                <span className="font-mono text-sm text-gray-900">{m.number}</span>
                {m.label && <span className="ml-2 text-sm text-gray-600">{m.label}</span>}
              </div>
              {canEdit && (
                <button
                  type="button"
                  disabled={drop.isPending}
                  onClick={() => drop.mutate(m.id)}
                  title="Stops it ringing on the next build. The group on the PBX keeps ringing it until then."
                  className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* WHY THEY ARE NOT RINGING, when they are not. A number this line rings only through a ring
          group does nothing while the number answers with a menu, and an operator who added one has
          every right to wonder why their mobile is silent. */}
      {!ringsToday && (
        <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          These will not ring until this number rings a group.
        </p>
      )}

      {!externals.length && !adding && (
        <p className="mt-2 text-xs text-gray-400">None.</p>
      )}

      {adding && (
        <div className="mt-3 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <input
            aria-label="Number to ring"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="13038881110"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            aria-label="Label for this number"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Whose phone is it? e.g. Lee’s cell"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          {save.isError && (
            <p className="text-sm text-red-700">{save.error.body?.error || save.error.message}</p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!number.trim() || save.isPending}
              onClick={() => save.mutate({
                num: number.replace(/\D/g, ''),
                lbl: label.trim() || null,
                // After the extensions by default: a desk phone that loses its place to a mobile is
                // the wrong way round for the hunt strategies.
                position: 90 + externals.length,
              })}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setNumber(''); setLabel(''); save.reset(); }}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
